import { create } from 'zustand';
import apiClient from '../lib/api';

export interface HrPolicy {
    id: string;
    tenantId: string;
    title: string;
    category: string;
    content: string;
    version: string;
    effectiveDate: string;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface PolicyState {
    policies: HrPolicy[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchPolicies: (filters?: any) => Promise<void>;
    createPolicy: (data: Partial<HrPolicy>) => Promise<void>;
    updatePolicy: (id: string, data: Partial<HrPolicy>) => Promise<void>;
    deletePolicy: (id: string) => Promise<void>;
}

export const usePolicyStore = create<PolicyState>((set, get) => ({
    policies: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
    },
    fetchPolicies: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/policies', { params: filters });
            const { data, pagination } = response.data;
            set({ policies: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch policies', isLoading: false });
        }
    },
    createPolicy: async (data: Partial<HrPolicy>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/people/policies', data);
            const newPolicy = response.data;
            set((state) => ({
                policies: [newPolicy, ...state.policies].sort((a, b) => a.title.localeCompare(b.title)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create policy', isLoading: false });
            throw error;
        }
    },
    updatePolicy: async (id: string, data: Partial<HrPolicy>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/people/policies/${id}`, data);
            const updatedPolicy = response.data;
            set((state) => ({
                policies: state.policies.map((o) => (o.id === id ? updatedPolicy : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update policy', isLoading: false });
            throw error;
        }
    },
    deletePolicy: async (id: string) => {
        set({ error: null });
        try {
            await apiClient.delete(`/people/policies/${id}`);
            set((state) => ({
                policies: state.policies.filter((p) => p.id !== id),
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete policy' });
            throw error;
        }
    },
}));
